import React, { useMemo } from 'react';
import {
  DomainInUseStatusStatusEnum,
  LoginMembership,
  ResourceName,
  Subscription,
  WebsiteAppKind,
  WebsiteKind,
} from 'src/orchd-client';
import { FormikHelpers } from 'formik';
import sortBy from 'lodash/sortBy';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useCheckDomainMutation } from 'src/api_services/domains/query';
import { useGetActiveSubscriptionsQuery } from 'src/api_services/subscriptions/query';
import { useCloneWebsiteMutation } from 'src/api_services/websites/query';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card, CardBody, CardFooter } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Modal } from 'src/components/Modal';
import { SelectField } from 'src/components/SelectNew';
import { DEFAULT_OPTION } from 'src/components/ServerSelect/ServerSelect';
import { ServerSelectContainer } from 'src/components/ServerSelect/ServerSelectContainer';
import { H3, Text } from 'src/components/Text';
import { TextInputField } from 'src/components/TextInput';
import { Form } from 'src/containers/Form';
import { messages } from 'src/messages';
import { getErrorInfo } from 'src/utils/errors';
import { getSelectFieldValue } from 'src/utils/helpers';
import { useHideModal } from 'src/utils/modal/useModal';
import { getSubscriptionResource, isUnlimitedResourceTotal } from 'src/utils/packages/utils';
import { parseServerIdFields } from 'src/utils/parseServerIdFields/parseServerIdFields';
import { useToast } from 'src/utils/toast/useToast';

import { getSchema, PushLiveToNewFields } from './schema';
import { Radio } from 'src/components/Radio';
import { useGetWebsiteAppsQuery } from 'src/api_services/apps/query';

interface Props {
  member: LoginMembership;
  websiteId: string;
  isCloning: boolean;
  stagingDomain?: string;
}

export const PushLiveToNewModal = ({ member, websiteId, isCloning, stagingDomain }: Props) => {
  const toast = useToast();
  const { formatMessage } = useIntl();
  const hide = useHideModal();
  const { push } = useHistory();
  const { orgId, isMasterOrg } = member;

  const { data: apps = [] } = useGetWebsiteAppsQuery({ orgId, websiteId });
  const { mutateAsync: cloneWebsite } = useCloneWebsiteMutation();
  const { mutateAsync: checkDomain } = useCheckDomainMutation();

  const { data: subscriptions } = useGetActiveSubscriptionsQuery({ orgId, enabled: !isMasterOrg });

  const getRemainingWebsites = (subscription: Subscription) => {
    const resource = getSubscriptionResource(subscription, ResourceName.websites);

    if (!resource) {
      return 0;
    }

    if (isUnlimitedResourceTotal(resource.total)) {
      return -1;
    }

    const remaining = (resource.total || 0) - resource.usage;

    return remaining || 0;
  };

  const getSubscriptionUsage = (subscription: Subscription) => {
    const resource = getSubscriptionResource(subscription, ResourceName.websites);

    if (!resource) {
      return { usage: 0, total: 0 };
    }

    if (isUnlimitedResourceTotal(resource.total)) {
      return { usage: resource.usage, total: -1 };
    }

    return { usage: resource.usage, total: resource.total || 0 };
  };

  const subscriptionsOptions = useMemo(
    () =>
      sortBy(subscriptions, (subscription) => -getRemainingWebsites(subscription)).map((subscription) => {
        const disabled = !!subscription.suspendedBy;

        return {
          value: subscription.id,
          label: subscription.friendlyName || subscription.planName,
          isDisabled: disabled,
          data: subscription,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptions]
  );

  const handleSubmit = async (values: PushLiveToNewFields, form: FormikHelpers<PushLiveToNewFields>) => {
    const { domain, subscription } = values;
    const subscriptionId = getSelectFieldValue(subscription);
    const serverIds = parseServerIdFields(values, member.isMasterOrg);

    try {
      const {
        data: { status },
      } = await checkDomain({ orgId, newDomain: { domain: values.isStaging ? `${domain}.${stagingDomain}` : domain } });

      if (status === DomainInUseStatusStatusEnum.prohibited) {
        return form.setFieldError('domain', formatMessage(messages.domain_prohibited_by_platform));
      }

      if (
        status === DomainInUseStatusStatusEnum.inUseCurrentOrg ||
        status === DomainInUseStatusStatusEnum.inUseAnotherOrg
      ) {
        return form.setFieldError(
          'domain',
          formatMessage({
            id: 'websites.push_live_modal.validation.domain.exists',
            defaultMessage: 'This domain already exists on the platform',
          })
        );
      }

      const {
        data: { id: cloneId },
      } = await cloneWebsite({
        orgId,
        websiteCloneRequest: {
          sourceWebsiteId: websiteId,
          excludePaths: [],
          deleteFilesFromDestination: false,
          syncPhpVersion: false,
          runWpSearchReplace: values.runWpSearchReplace,
          newWebsite: {
            domain,
            kind: values.isStaging ? WebsiteKind.staging : WebsiteKind.normal,
            subscriptionId: isMasterOrg ? undefined : Number(subscriptionId),
            ...serverIds,
          },
        },
      });

      isCloning ? push(`/websites/clone/${cloneId}`) : push(`/websites/push-live/${cloneId}`);

      hide();
    } catch (e) {
      const { message } = getErrorInfo(e);

      toast.error(
        isCloning
          ? formatMessage(
              { id: 'websites.push_live_modal.clone_error', defaultMessage: 'Failed to clone website: {error}' },
              { error: message }
            )
          : formatMessage(
              { id: 'websites.push_live_to_existing.error', defaultMessage: 'Failed to push website live: {error}' },
              { error: message }
            )
      );
    }
  };

  return (
    <Modal>
      <Form<PushLiveToNewFields>
        initialValues={{
          domain: '',
          subscription: [],
          serverIds: {
            application: [DEFAULT_OPTION],
            email: [DEFAULT_OPTION],
            backup: [DEFAULT_OPTION],
            database: [DEFAULT_OPTION],
            dns: [DEFAULT_OPTION],
            postgresql: [DEFAULT_OPTION],
          },
          runWpSearchReplace: true,
          isStaging: false,
        }}
        onSubmit={(values, form) => handleSubmit(values, form)}
        validationSchema={getSchema(formatMessage, isMasterOrg)}
        validateOnMount
      >
        {(form) => {
          const stagingResource =
            subscriptions &&
            subscriptions
              .find((sub) => sub.id === form.values.subscription[0]?.value)
              ?.resources.find((resource) => resource.name === ResourceName.stagingWebsites);

          const productionResource =
            subscriptions &&
            subscriptions
              .find((sub) => sub.id === form.values.subscription[0]?.value)
              ?.resources.find((resource) => resource.name === ResourceName.websites);

          const stagingTotal = stagingResource ? stagingResource.total || -1 : 0;
          const stagingUsage = stagingResource ? stagingResource.usage : 0;

          const productionTotal = productionResource ? productionResource.total || -1 : 0;
          const productionUsage = productionResource ? productionResource.usage : 0;

          const showStaging = stagingDomain && (isMasterOrg || stagingTotal === -1 || stagingTotal > 0);

          const submitDisabledStaging =
            form.values.isStaging && stagingUsage >= stagingTotal && stagingTotal !== -1 && !isMasterOrg;
          const submitDisabledProduction =
            !form.values.isStaging && productionUsage >= productionTotal && productionTotal !== -1 && !isMasterOrg;

          if (stagingResource && submitDisabledProduction && !submitDisabledStaging) {
            form.setFieldValue('isStaging', true);
          }

          return (
            <Card marginBottom="lg">
              <CardBody>
                <Box j="center" p="lg">
                  <H3 marginBottom="2xl">
                    {isCloning ? (
                      <FormattedMessage id="websites.push_live_modal.clone_title" defaultMessage="Clone website" />
                    ) : (
                      <FormattedMessage
                        id="websites.push_live_modal.title"
                        defaultMessage="Push staging website live"
                      />
                    )}
                  </H3>

                  <Text size="sm" marginBottom="2xl">
                    {isCloning ? (
                      <FormattedMessage
                        id="websites.push_live_modal.target_domain"
                        defaultMessage="Enter the domain to which you would like to clone this website."
                      />
                    ) : (
                      <FormattedMessage
                        id="websites.push_live_modal.body"
                        defaultMessage="Enter your production domain name to push your staging domain live. Pushing a staging website live will consume 1 website from your package resources."
                      />
                    )}
                  </Text>

                  {!isMasterOrg && (
                    <Box mb="xl">
                      <SelectField
                        name="subscription"
                        placeholder={formatMessage(messages.select_a_package)}
                        label={formatMessage(messages.package)}
                        value={form.values.subscription}
                        err={form.errors.subscription}
                        touched={form.touched.subscription}
                        onChange={({ name, value }) => {
                          form.setFieldValue('isStaging', false);
                          form.setFieldValue(name, value);
                        }}
                        options={subscriptionsOptions}
                        requiredIndicator
                        clearable
                        searchable
                        estimatedOptionHeight={56}
                        renderOption={(option) => {
                          const subscription = option.option.data as Subscription;
                          const isDisabled = option.option.isDisabled;
                          const search = option.search;

                          return (
                            <Box>
                              <Box wrap="nowrap" j="space-between">
                                <Text
                                  ellipsis
                                  fullWidth
                                  color={isDisabled ? 'disabled' : 'dark'}
                                  highlightText={search}
                                >
                                  {subscription.friendlyName || subscription.planName}
                                </Text>
                              </Box>

                              <Text size="sm" color="grey">
                                <FormattedMessage
                                  id="websites.push_live_modal.website_usage"
                                  defaultMessage="{usage} of {total, plural, =-1 {∞} other {#}} websites used"
                                  values={{ ...getSubscriptionUsage(subscription) }}
                                />
                              </Text>
                            </Box>
                          );
                        }}
                      />
                    </Box>
                  )}

                  {showStaging && (
                    <Box mb="md" d="column">
                      <Radio
                        name="isStaging"
                        onChange={() => {
                          form.setFieldValue('isStaging', false);
                        }}
                        checked={!form.values.isStaging}
                        label={
                          isMasterOrg ? (
                            <FormattedMessage
                              id="websites.push_live_modal.kind_radios.production"
                              defaultMessage="Production website"
                            />
                          ) : (
                            <Text
                              color={
                                productionUsage >= productionTotal && productionTotal !== -1 && !isMasterOrg
                                  ? 'error'
                                  : undefined
                              }
                            >
                              <FormattedMessage
                                id="websites.push_live_modal.kind_radios.staging_with_usage"
                                defaultMessage="Production website ({productionUsage} of {productionTotal} used)"
                                values={{
                                  productionUsage,
                                  productionTotal: productionResource ? productionResource.total || '∞' : '0',
                                }}
                              />
                            </Text>
                          )
                        }
                      />
                      <Radio
                        name="isStaging"
                        onChange={() => {
                          form.setFieldValue('isStaging', true);
                        }}
                        checked={form.values.isStaging}
                        label={
                          isMasterOrg ? (
                            <FormattedMessage
                              id="websites.push_live_modal.kind_radios.staging"
                              defaultMessage="Staging website"
                            />
                          ) : (
                            <Text
                              color={
                                stagingUsage >= stagingTotal && stagingTotal !== -1 && !isMasterOrg
                                  ? 'error'
                                  : undefined
                              }
                            >
                              <FormattedMessage
                                id="websites.push_live_modal.kind_radios.staging_with_usage"
                                defaultMessage="Staging website ({stagingUsage} of {stagingTotal} used)"
                                values={{ stagingUsage, stagingTotal: stagingTotal || '∞' }}
                              />
                            </Text>
                          )
                        }
                      />
                    </Box>
                  )}

                  <Box mb="lg">
                    <TextInputField
                      name="domain"
                      label="Domain"
                      err={form.errors.domain}
                      touched={form.touched.domain}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                      value={form.values.domain}
                      requiredIndicator
                      prefix="www."
                      suffix={form.values.isStaging && stagingDomain}
                    />
                  </Box>

                  {apps.find((app) => app.app === WebsiteAppKind.wordpress) && (
                    <Checkbox
                      name="runWpSearchReplace"
                      label={formatMessage({
                        id: 'run_wp_search_replace',
                        defaultMessage: 'Perform a WordPress search and replace',
                      })}
                      size="sm"
                      fontSize="sm"
                      checked={form.values.runWpSearchReplace}
                      onChange={({ name, checked }) => form.setFieldValue(name, checked)}
                      infoTooltip={formatMessage({
                        id: 'run_wp_search_replace.tooltip',
                        defaultMessage:
                          'Using wp-cli this will search every table in the database replacing the old domain with the new domain.',
                      })}
                    />
                  )}

                  {isMasterOrg && (
                    <Box mt="xl">
                      <ServerSelectContainer />
                    </Box>
                  )}
                </Box>
              </CardBody>

              <CardFooter>
                <Box a="center" px="lg" j="flex-end" c={{ w: 'auto', ml: '2xl' }}>
                  <Button variant="tertiary" size="small" onClick={hide}>
                    <FormattedMessage {...messages.cancel} />
                  </Button>

                  <Button
                    type="submit"
                    size="small"
                    faded={!form.isValid || submitDisabledStaging || submitDisabledProduction}
                    loading={form.isSubmitting}
                    disabled={!form.isValid || submitDisabledStaging || submitDisabledProduction}
                  >
                    {isCloning ? (
                      <FormattedMessage {...messages.clone} />
                    ) : (
                      <FormattedMessage {...messages.push_live} />
                    )}
                  </Button>
                </Box>
              </CardFooter>
            </Card>
          );
        }}
      </Form>
    </Modal>
  );
};
